/* eslint-disable ts/no-unsafe-member-access */
/* eslint-disable ts/no-unsafe-assignment */
/* eslint-disable ts/no-explicit-any */
/* eslint-disable ts/no-unsafe-argument */
import type { useForm } from "react-hook-form"
import type { Dependency, FieldConfig, FieldConfigItem } from "../types"
import { useFormContext } from "react-hook-form"
import * as z from "zod"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { FormField } from "@/components/ui/form"
import { cn } from "@/lib/utils"
import { DEFAULT_ZOD_HANDLERS, INPUT_COMPONENTS } from "../config"
import resolveDependencies from "../dependencies"
import {
  beautifyObjectName,
  getBaseSchema,
  getBaseType,
  sortFieldsByOrder,
  zodToHtmlInputProps,
} from "../utils"
import AutoFormArray from "./array"

function DefaultParent({ children }: { children: React.ReactNode }) {
  return <>{children}</>
}

export default function AutoFormObject<
  SchemaType extends z.ZodObject<any, any>,
>({
  schema,
  form,
  fieldConfig,
  path = [],
  dependencies = [],
  columns = 1,
  layout = "vertical",
  containerClassName,
}: {
  schema: SchemaType | z.ZodEffects<SchemaType>
  form: ReturnType<typeof useForm>
  fieldConfig?: FieldConfig<z.infer<SchemaType>>
  path?: string[]
  dependencies?: Dependency<z.infer<SchemaType>>[]
  columns?: number
  layout?: "vertical" | "grid"
  containerClassName?: string
}) {
  const { watch } = useFormContext() // Use useFormContext to access the watch function

  if (!schema) {
    return null
  }
  const { shape } = getBaseSchema<SchemaType>(schema) || {}

  if (!shape) {
    return null
  }

  const handleIfZodNumber = (item: z.ZodAny) => {
    const isZodNumber = (item as any)._def.typeName === "ZodNumber"
    const isInnerZodNumber
      = (item._def as any).innerType?._def?.typeName === "ZodNumber"

    if (isZodNumber) {
      (item as any)._def.coerce = true
    }
    else if (isInnerZodNumber) {
      (item._def as any).innerType._def.coerce = true
    }

    return item
  }

  const sortedFieldKeys = sortFieldsByOrder(fieldConfig, Object.keys(shape))

  const gridColsClass = `grid-cols-1 md:grid-cols-${Math.max(columns, 1)}`

  return (
    <Accordion className={cn(layout === "grid" ? `grid ${gridColsClass} gap-4` : "space-y-5", containerClassName)} type="multiple">
      {sortedFieldKeys.map((name) => {
        let item = shape[name] as z.ZodAny
        item = handleIfZodNumber(item)
        const zodBaseType = getBaseType(item)
        const itemName = item._def.description ?? beautifyObjectName(name)
        const key = [...path, name].join(".")

        const {
          isHidden,
          isDisabled,
          isRequired: isRequiredByDependency,
          overrideOptions,
        } = resolveDependencies(dependencies, name, watch)
        if (isHidden) {
          return null
        }

        if (zodBaseType === "ZodObject" && fieldConfig?.[name]?.fieldType !== "rangePicker") {
          return (
            <AccordionItem
              key={key}
              className="border-none"
              value={name}
            >
              <AccordionTrigger>{itemName}</AccordionTrigger>
              <AccordionContent className="p-2">
                <AutoFormObject
                  fieldConfig={
                    (fieldConfig?.[name] ?? {}) as FieldConfig<
                      z.infer<typeof item>
                    >
                  }
                  form={form}
                  path={[...path, name]}
                  schema={item as unknown as z.ZodObject<any, any>}
                />
              </AccordionContent>
            </AccordionItem>
          )
        }
        if (zodBaseType === "ZodArray" && fieldConfig?.[name]?.fieldType !== "extendedOptions") {
          return (
            <AutoFormArray
              key={key}
              fieldConfig={fieldConfig?.[name] ?? {}}
              form={form}
              item={item as unknown as z.ZodArray<any>}
              name={name}
              path={[...path, name]}
            />
          )
        }

        const fieldConfigItem: FieldConfigItem = fieldConfig?.[name] ?? {}
        const zodInputProps = zodToHtmlInputProps(item)
        const isRequired
          = isRequiredByDependency
            || zodInputProps.required
            || fieldConfigItem.inputProps?.required
            || false

        if (overrideOptions) {
          item = z.enum(overrideOptions) as unknown as z.ZodAny
        }

        return (
          <FormField
            key={key}
            control={form.control as any}
            name={key}
            render={({ field }) => {
              const inputType
                = fieldConfigItem.fieldType
                  ?? DEFAULT_ZOD_HANDLERS[zodBaseType]
                  ?? "fallback"

              const InputComponent
                = typeof inputType === "function"
                  ? inputType
                  : INPUT_COMPONENTS[inputType]

              const ParentElement
                = fieldConfigItem.renderParent ?? DefaultParent

              const defaultValue = fieldConfigItem.inputProps?.defaultValue
              const value = field.value ?? defaultValue ?? ""

              const fieldProps = {
                ...zodToHtmlInputProps(item),
                ...field,
                ...fieldConfigItem.inputProps,
                disabled: fieldConfigItem.inputProps?.disabled || isDisabled,
                ref: undefined,
                value,
              }

              if (InputComponent === undefined) {
                return <></>
              }

              if (fieldProps.hidden) {
                return <span />
              }

              return (
                <ParentElement key={`${key}.parent`}>
                  <InputComponent
                    className={fieldProps.className}
                    field={field}
                    fieldConfigItem={fieldConfigItem}
                    fieldProps={fieldProps}
                    isRequired={isRequired}
                    label={itemName}
                    zodInputProps={zodInputProps}
                    zodItem={item}
                  />
                </ParentElement>
              )
            }}
          />
        )
      })}
    </Accordion>
  )
}
